import { useEffect, useState } from "react";
import { Body } from "../Common/Typography";
import { List, PillContainer } from "./__styles__/MultiselectPill";
import Wrapper from "./Wrapper";

const Pill = ({
  content,
  selectedItems,
  onChange,
  disabled,
  readonly,
}: {
  content: string;
  selectedItems: Array<string>;
  onChange: any;
  disabled: boolean;
  readonly: boolean;
}) => {
  const [selected, setSelected] = useState(selectedItems.includes(content));

  const handleOnChange = () => {
    if (disabled || readonly) return;

    setSelected(!selected);
    const index = selectedItems.indexOf(content);
    if (index === -1) {
      selectedItems.push(content);
    } else {
      selectedItems.splice(index, 1);
    }
    onChange(selectedItems);
  };

  useEffect(() => {
    // when default values are set asynchronously, ensure the item appears as selected
    setSelected(selectedItems.includes(content));
  }, [selectedItems, content]);

  return (
    <PillContainer
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled || readonly}
      onClick={handleOnChange}
      onKeyDown={e => {
        if (e.key === "Enter") handleOnChange();
      }}
      tabIndex={disabled || readonly ? -1 : 0}
      selected={selected}
      disabled={disabled || readonly}
      readonly={readonly}
    >
      <Body
        size="large"
        type="regular"
        color={selected ? "contentPrimaryInverted" : "contentPrimary"}
      >
        {content}
      </Body>
    </PillContainer>
  );
};

export interface MultiselectPillProps {
  items: Array<string>;
  selectedItems: Array<string>;
  onChange: any;
  name: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  label?: Maybe<string>;
  error?: string;
  description?: Maybe<string>;
  tooltip?: string;
}

export const MultiselectPill = ({
  items,
  selectedItems = [],
  onChange,
  name,
  required,
  disabled = false,
  readonly = false,
  label,
  error,
  description,
  tooltip,
}: MultiselectPillProps) => (
  <Wrapper
    name={name}
    required={required}
    label={label}
    error={error}
    description={description}
    tooltip={tooltip}
    printValue={selectedItems.join(", ")}
  >
    <List hasTooltip={!!tooltip} gap="s">
      {items.map((item, index) => (
        <Pill
          content={item}
          selectedItems={selectedItems}
          onChange={onChange}
          disabled={disabled}
          readonly={readonly}
          key={index}
        />
      ))}
    </List>
  </Wrapper>
);
